/**
 * `EngagementSection` — `AdminMetricsOverview.engagement` as three
 * honest stat tiles. Computed LIVE in the overview route over the
 * SAME `[from, to)` window as the rest of the dashboard.
 *
 * Tiles:
 *   - Events per active account (`events_per_active_account`)
 *     — engagement intensity: "how loud is each active merchant"
 *   - Median active tenure days (`median_active_tenure_days`)
 *     — "how long the typical active account has been engaged"
 *   - Avg active days per account (`avg_active_days_per_account`)
 *     — stickiness: "how many distinct days per merchant in the window"
 *
 * Each tile shows a clear unit (events / days / days) so the figure
 * is unambiguous on a screenshot. NULL values render as "—" — the
 * server emits NULL when there are no active accounts in the window
 * (no denominator); the UI must NEVER fabricate a `0`.
 */

import type { AdminMetricsEngagement } from '@sill/types';

import { Card } from '../../../components/atoms/Card.js';
import {
  formatDays,
  formatDecimal,
} from '../../../lib/format.js';
import { useC } from '../../../lib/theme.js';
import { SectionHeader } from '../KpiTile.js';

const monoFont = "'JetBrains Mono', monospace";
const sansFont = "'Geist', sans-serif";

export type EngagementSectionState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; engagement: AdminMetricsEngagement };

export interface EngagementSectionProps {
  state: EngagementSectionState;
}

const CAPTION =
  'Honest signals over the selected window. "—" means no active accounts to measure (no denominator), not zero.';

export function EngagementSection({ state }: EngagementSectionProps): JSX.Element {
  return (
    <section>
      <SectionHeader
        kicker="Engagement"
        title="Activity intensity & tenure"
        caption={CAPTION}
      />
      <EngagementBody state={state} />
    </section>
  );
}

function EngagementBody({ state }: { state: EngagementSectionState }): JSX.Element {
  const C = useC();

  if (state.kind === 'loading') {
    return (
      <Card style={{ padding: 24 }}>
        <span
          style={{
            fontFamily: monoFont,
            fontSize: 11,
            color: C.dim,
            letterSpacing: 1.5,
          }}
        >
          Loading engagement…
        </span>
      </Card>
    );
  }

  if (state.kind === 'error') {
    return (
      <Card style={{ padding: 18 }}>
        <div
          style={{
            fontFamily: monoFont,
            fontSize: 10,
            color: C.coralFg,
            letterSpacing: 2,
            textTransform: 'uppercase',
            marginBottom: 6,
          }}
        >
          Engagement unavailable
        </div>
        <div style={{ fontFamily: sansFont, fontSize: 13, color: C.body }}>
          {state.message}
        </div>
      </Card>
    );
  }

  const e = state.engagement;

  const tenureUnitValue =
    e.median_active_tenure_days === null
      ? undefined
      : tenureUnit(e.median_active_tenure_days);
  const avgDaysUnit =
    e.avg_active_days_per_account === null ? undefined : 'days';

  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
      <StatCard
        kicker="Events per active account"
        value={formatDecimal(e.events_per_active_account, 1)}
        unit="events"
        sub="audit_record / distinct active accounts"
        accent={C.limeFg}
      />
      <StatCard
        kicker="Median active tenure"
        value={
          e.median_active_tenure_days === null
            ? '—'
            : formatTenureValue(e.median_active_tenure_days)
        }
        {...(tenureUnitValue !== undefined ? { unit: tenureUnitValue } : {})}
        sub="LAST − FIRST real audit_record, median across cohort"
        accent={C.mintFg}
      />
      <StatCard
        kicker="Avg active days per account"
        value={
          e.avg_active_days_per_account === null
            ? '—'
            : formatDecimal(e.avg_active_days_per_account, 1)
        }
        {...(avgDaysUnit !== undefined ? { unit: avgDaysUnit } : {})}
        sub="Distinct UTC days with ≥1 real record, mean per account"
        accent={C.skyFg}
      />
    </div>
  );
}

/**
 * Format the tenure NUMBER (just the headline value, no unit suffix —
 * `StatCard` renders the unit chip separately). For tenure < 1 day we
 * show the full `formatDays` output (`<1 day`) without a unit.
 */
function formatTenureValue(days: number): string {
  if (days < 1) {
    // formatDays already returns "<1 day" / "0 days" — present in full,
    // and let the unit slot below be hidden.
    return formatDays(days);
  }
  // Whole days for the headline (single decimal under 10, integer above).
  if (days < 10) return days.toFixed(1);
  return Math.round(days).toLocaleString('en-US');
}

function tenureUnit(days: number): string | undefined {
  if (days < 1) return undefined; // full label already in the value
  if (days < 2) return 'day';
  return 'days';
}

interface StatCardProps {
  kicker: string;
  value: string;
  unit?: string;
  sub: React.ReactNode;
  accent: string;
}

function StatCard({ kicker, value, unit, sub, accent }: StatCardProps): JSX.Element {
  const C = useC();
  return (
    <Card style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div
        style={{
          fontFamily: monoFont,
          fontSize: 9,
          color: accent,
          letterSpacing: 2,
          textTransform: 'uppercase',
        }}
      >
        {kicker}
      </div>
      <div className="flex items-baseline" style={{ gap: 6, flexWrap: 'wrap' }}>
        <div
          style={{
            fontFamily: monoFont,
            fontSize: 28,
            color: C.text,
            fontWeight: 500,
            letterSpacing: -0.6,
            lineHeight: 1.1,
          }}
        >
          {value}
        </div>
        {unit !== undefined ? (
          <div
            style={{
              fontFamily: monoFont,
              fontSize: 11,
              color: C.muted,
              letterSpacing: 1,
              textTransform: 'uppercase',
            }}
          >
            {unit}
          </div>
        ) : null}
      </div>
      <div
        style={{
          fontFamily: sansFont,
          fontSize: 11,
          color: C.muted,
          lineHeight: 1.5,
        }}
      >
        {sub}
      </div>
    </Card>
  );
}
